/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ProductPermissionEnum } from './ProductPermissionEnum';
import {
    ProductPermissionEnumFromJSON,
    ProductPermissionEnumFromJSONTyped,
    ProductPermissionEnumToJSON,
} from './ProductPermissionEnum';
import type { PayoutEventTypeEnum } from './PayoutEventTypeEnum';
import {
    PayoutEventTypeEnumFromJSON,
    PayoutEventTypeEnumFromJSONTyped,
    PayoutEventTypeEnumToJSON,
} from './PayoutEventTypeEnum';

/**
 * 
 * @export
 * @interface AdminPayoutEvent
 */
export interface AdminPayoutEvent {
    /**
     * 
     * @type {string}
     * @memberof AdminPayoutEvent
     */
    readonly id: string;
    /**
     * 
     * @type {PayoutEventTypeEnum}
     * @memberof AdminPayoutEvent
     */
    payoutEventType: PayoutEventTypeEnum;
    /**
     * 
     * @type {Date}
     * @memberof AdminPayoutEvent
     */
    paymentDate: Date;
    /**
     * 
     * @type {string}
     * @memberof AdminPayoutEvent
     */
    readonly productId: string;
    /**
     * 
     * @type {string}
     * @memberof AdminPayoutEvent
     */
    readonly productName: string | null;
    /**
     * 
     * @type {string}
     * @memberof AdminPayoutEvent
     */
    issuer?: string | null;
    /**
     * 
     * @type {Array<ProductPermissionEnum>}
     * @memberof AdminPayoutEvent
     */
    readonly productPermissions: Array<ProductPermissionEnum>;
    /**
     * 
     * @type {Date}
     * @memberof AdminPayoutEvent
     */
    readonly createdAt: Date;
}



/**
 * Check if a given object implements the AdminPayoutEvent interface.
 */
export function instanceOfAdminPayoutEvent(value: object): value is AdminPayoutEvent {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('payoutEventType' in value) || value['payoutEventType'] === undefined) return false;
    if (!('paymentDate' in value) || value['paymentDate'] === undefined) return false;
    if (!('productId' in value) || value['productId'] === undefined) return false;
    if (!('productName' in value) || value['productName'] === undefined) return false;
    if (!('productPermissions' in value) || value['productPermissions'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    return true;
}

export function AdminPayoutEventFromJSON(json: any): AdminPayoutEvent {
    return AdminPayoutEventFromJSONTyped(json, false);
}

export function AdminPayoutEventFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminPayoutEvent {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'payoutEventType': PayoutEventTypeEnumFromJSON(json['payout_event_type']),
        'paymentDate': (new Date(json['payment_date'])),
        'productId': json['product_id'],
        'productName': json['product_name'],
        'issuer': json['issuer'] == null ? undefined : json['issuer'],
        'productPermissions': ((json['product_permissions'] as Array<any>).map(ProductPermissionEnumFromJSON)),
        'createdAt': (new Date(json['created_at'])),
    };
}

export function AdminPayoutEventToJSON(value?: Omit<AdminPayoutEvent, 'id'|'product_id'|'product_name'|'product_permissions'|'created_at'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'payout_event_type': PayoutEventTypeEnumToJSON(value['payoutEventType']),
        'payment_date': ((value['paymentDate']).toISOString().substring(0,10)),
        'issuer': value['issuer'],
    };
}

